import Message from '@sportnet/ui/lib/Message';
import { isAfter } from 'date-fns';
import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { compose } from 'redux';
import { useAsyncData } from 'ssr-service';
import { thunkToAction } from 'typescript-fsa-redux-thunk';
import AdminLayoutContext from '../../components/AdminLayout/context';
import EventHeader from '../../components/EventHeader';
import { Content } from '../../components/Layout';
import { RootState } from '../../configureStore';
import { authUserProfileSelector } from '../../containers/App/selectors';
import { loadEventInvitationById } from '../../containers/Events/actions';
import { eventByIdSelector } from '../../containers/Events/selectors';
import Loading from '../../containers/Loading';
import { ITheme } from '../../theme/theme';
import __ from '../../utilities/__';
import EventInvitationStatusChange from './changeStatus';

const mapDispatchToProps = {
  loadEvent: thunkToAction(loadEventInvitationById.action),
};

type Props = { theme: ITheme } & typeof mapDispatchToProps &
  RouteComponentProps<{ eventId: string; invitationId: string }, {}>;

const mapStateToProps = (state: RootState, props: Props) => ({
  event: eventByIdSelector(props.params.eventId)(state),
  authUser: authUserProfileSelector(state),
});

const EventInvitation: React.FC<ReturnType<typeof mapStateToProps> & Props> = ({
  router: { push },
  authUser,
  params: { eventId, invitationId },
  loadEvent,
  event,
  theme,
}) => {
  const [isFetching, setIsFetching] = React.useState(false);

  useAsyncData(async () => {
    await loadEventData();
  }, [eventId]);

  const loadEventData = async () => {
    setIsFetching(true);
    try {
      await loadEvent({ eventId, invitationId });
    } catch (e) {
      if (authUser) {
        push('/admin/events/me');
      } else {
        push('/');
      }
    } finally {
      setIsFetching(false);
    }
  };

  const { setHeaderComponents, headerComponents } = React.useContext(
    AdminLayoutContext,
  );
  const componentHeader = __('Zápasy');
  const componentSubHeader = __('Detail zápasu');

  if (
    headerComponents.header !== componentHeader ||
    headerComponents.subHeader !== componentSubHeader
  ) {
    setHeaderComponents(componentHeader, componentSubHeader);
  }

  if (!event || isFetching || !authUser) {
    return <Loading />;
  }
  const isPlayer = (event.players || []).find(p => p._id === authUser._id);
  const isCreator = event.createdBy && event.createdBy._id === authUser._id;

  const freeCapacity = event.capacity - (event.players || []).length;
  const startDateIsDue = isAfter(new Date(), new Date(event.startDate));
  const registrationDateIsDue =
    event.registrationDueDate &&
    isAfter(new Date(), new Date(event.registrationDueDate));

  return (
    <Content>
      {event.createdBy && (
        <Message primary>
          {event.createdBy.name} {event.createdBy.surname}{' '}
          {__('vás pozýva na túto udalosť')}
        </Message>
      )}
      {!isPlayer && !freeCapacity && (
        <Message danger>
          {__('Ľutujeme, všetky voľné miesta sú už obsadené')}
        </Message>
      )}
      {!isPlayer &&
        freeCapacity > 0 &&
        (startDateIsDue || registrationDateIsDue) && (
          <Message danger>
            {__('Ľutujeme, prihlasovanie sa do zápasu už skončilo')}
          </Message>
        )}
      <EventHeader
        event={event}
        isPlayer={!!isPlayer}
        isCreator={!!isCreator}
      />
      <EventInvitationStatusChange
        event={event}
        reloadEvent={() => {
          push(`/admin/event/${event._id}`);
        }}
        invitationId={invitationId}
      />
    </Content>
  );
};

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(EventInvitation);
